import React, { useState, useRef, useEffect } from 'react';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons'; 
import { FolderData } from "../../types/PanoptoData";

interface PanoptoConnectionTabProps {
  sortedFolderData: FolderData | undefined;
  setPanoptos: React.Dispatch<React.SetStateAction<FolderData[]>>;
  selectedFolder: number;
  triggerAutoSave: () => void;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
  systemConfig: { Id: string; ConfigValue: string; }[];
  safeAiIconSrc: string;
  assistant: any;
  isGettingDataCalled: boolean;
  handleConnect: (panoptoId: number, source: 'click' | 'loop') => void;
  panoptos: FolderData[];
}

const PanoptoConnectionTab: React.FC<PanoptoConnectionTabProps> = ({
  sortedFolderData,
  setPanoptos,
  selectedFolder,
  triggerAutoSave,
  setPendingSave,
  systemConfig,
  safeAiIconSrc,
  assistant,
  isGettingDataCalled,
  handleConnect,
  panoptos
}) => {
  const [showHowImage, setShowHowImage] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [modalSize, setModalSize] = useState('max-w-[40rem]');
  const imageModalRef = useRef<HTMLDivElement>(null);

  const KaiBlobLocation = systemConfig.find(item => item.Id === 'blob_root_folder')?.ConfigValue;

  // Close image modal on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (imageModalRef.current && !imageModalRef.current.contains(event.target as Node)) {
        setShowHowImage(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUrlChange = (value: string) => {
    setPanoptos(prev => prev.map(panopto => 
      panopto.id === selectedFolder ? { ...panopto, url: value } : panopto
    ));
  };

  const handleConnect_Local = (panoptoId: number, source: 'click' | 'loop') => {
    const panopto = panoptos.find(c => c.id === panoptoId);
    
    if (!panopto?.url) {
      setPanoptos((prev) =>
        prev.map((c) =>
          c.id === panoptoId
            ? { ...c, UrlError: 'Please enter a valid URL.' }
            : c
        )
      );
      return;
    }

    const isCourseAlreadyConnected = panoptos.some(c => c.url === panopto.url && c.panopto_id !== "");

    if (isCourseAlreadyConnected) {
      setPanoptos((prev) =>
        prev.map((c) =>
          c.id === panoptoId
            ? { ...c, UrlError: 'This panopto folder is already connected.' }
            : c
        )
      );
      return;
    }

    // Clear any existing errors before attempting connection
    setPanoptos((prev) =>
      prev.map((c) =>
        c.id === panoptoId ? { ...c, UrlError: undefined } : c
      )
    );

    handleConnect(panoptoId, source);
  };

  return (
    <>
      <div className="border border-gray-300 rounded-md shadow-lg max-h-[47vh] overflow-y-auto">
        <div className="grid grid-cols-5 gap-6 p-4">
          {/* Left Column: Text Instructions */}
          <div className="col-span-3">
            <h3 className="text-sm font-semibold mb-6">
              Connect me with your Panopto folder.
              <br />
              You only have to do this once for this folder.
            </h3>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                  1
                </div>
                <div>
                  <p>Open a separate browser tab or window and navigate to your Panopto URL/folder.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                  2
                </div>
                <div>
                  <p>Add me to your Panopto folder as a <strong>VIEWER</strong>. My user ID is <strong>{systemConfig.find(item => item.Id === 'canvas_system_user')?.ConfigValue || ''}</strong>.</p>
                  <button
                    type="button"
                    className="text-gray-500 text-sm hover:underline mt-1 block p-0 bg-transparent border-none"
                    onClick={() => {
                      setImageSrc(`${KaiBlobLocation}/static/panopto_how_do_i.jpg`);
                      setShowHowImage(true);
                      setModalSize('max-w-[60rem]');
                    }}
                  >
                    How do I do that?
                  </button>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                  3
                </div>
                <div className="flex-1">
                  <p>Enter the Panopto folder Id in the textbox below.</p>
                  <button
                    type="button"
                    className="text-gray-500 text-sm hover:underline mt-1 block p-0 bg-transparent border-none"
                    onClick={(e) => {
                      e.preventDefault();
                      setImageSrc(`${KaiBlobLocation}/static/panopto_where_do_i.jpg`);
                      setShowHowImage(true);
                      setModalSize('max-w-[40rem]');
                    }}
                  >
                    Where do I find that?
                  </button>
                  <div className="flex items-center space-x-4 mt-2">
                    <input
                      type="text"
                      value={sortedFolderData?.url || ''}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      placeholder="Eg: c2f2360e-f37e-4765-8acd-ac340122335X"
                      className="w-full flex-1 px-3 py-2 border rounded-md"
                      disabled={sortedFolderData?.connectionState === 'connecting' || sortedFolderData?.connectionState === 'connected'}
                    />
                  </div>
                  {sortedFolderData?.UrlError && (
                    <p className="text-red-500 text-sm mt-2">{sortedFolderData.UrlError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Icons, Horizontal Dots, and Connect Button */}
          <div className="flex col-span-2 flex-col items-center justify-center space-y-4 h-full">
            {/* Two Icons with Horizontal Dots */}
            <div className="flex items-center space-x-4">
              {/* Left Icon */}
              {assistant?.app_ai_face ? (
                <img
                  src={safeAiIconSrc}
                  alt="AI Face"
                  className="h-8 w-8 cursor-pointer"
                />
              ) : (
                <FontAwesomeIcon icon={faRobot} className="h-8 w-8 text-black" />
              )}

              {/* Horizontal Dots */}
              <div className="flex items-center space-x-2">
                {[...Array(5)].map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1 w-1 rounded-full ${
                      sortedFolderData?.connectionState === 'connecting'
                        ? 'bg-black animate-pulse'
                        : 'bg-black'
                    }`}
                    style={
                      sortedFolderData?.connectionState === 'connecting'
                        ? { animationDelay: `${idx * 150}ms` }
                        : {}
                    }
                  />
                ))}
              </div>

              {/* Right Icon */}
              <img src="/panopto-logo.svg" alt="Panopto Logo" className="h-8 w-8" />
            </div>

            {/* Connect Button */}
            {sortedFolderData?.connectionState === 'idle' && (
              <button 
                onClick={() => selectedFolder && handleConnect_Local(selectedFolder, 'click')}
                className="bg-black text-white hover:text-white hover:border-b-4 hover:bg-black py-2 px-6 border rounded-full whitespace-nowrap"
              >
                CONNECT
              </button>
            )}
                    
            {sortedFolderData?.connectionState === 'connecting' && (
              <button 
                className="pl-10 bg-blue-600 text-white py-2 px-8 rounded-md flex items-center space-x-4 cursor-not-allowed"
                disabled
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Connecting...</span>
              </button>
            )}
                    
            {sortedFolderData?.connectionState === 'connected' && (
              <button 
                className="bg-green-600 text-white py-2 px-6 rounded-md flex items-center space-x-2 cursor-not-allowed"
                disabled
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Connected</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showHowImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            ref={imageModalRef}
            className={`relative bg-white px-6 pt-3 pb-1 rounded-lg shadow-lg w-full ${modalSize}`}
          >
            {/* Close Button Row */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowHowImage(false)}
                className="text-gray-600 hover:text-black font-bold focus:outline-none"
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Image */}
            <div className="mt-2">
              <img
                src={imageSrc}
                alt="Example"
                className="max-w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PanoptoConnectionTab;